/**
 * Composable compartilhado com funções auxiliares para templates de relatório.
 *
 * Cada template .vue importa estas funções em vez de reimplementá-las.
 * Garante consistência entre preview HTML e PDF exportado.
 *
 * @module useRelatorioImagens
 */

/**
 * Extrai a dataUrl de uma imagem, aceitando tanto string direta
 * quanto objeto { dataUrl, larguraOriginal, alturaOriginal, maxWidth, maxHeight }.
 *
 * @param {Object|string|null} imagem - Objeto da imagem ou string dataUrl
 * @returns {string|null} - dataUrl base64 válida ou null
 */
export function extrairDataUrl (imagem) {
  if (!imagem) return null
  if (typeof imagem === 'string') return imagem.startsWith('data:') ? imagem : null
  if (imagem.dataUrl && typeof imagem.dataUrl === 'string' && imagem.dataUrl.startsWith('data:')) return imagem.dataUrl
  return null
}

/**
 * Extrai a largura original do objeto imagem.
 * @param {Object|string|null} imagem
 * @returns {number|null}
 */
export function extrairLargura (imagem) {
  return (imagem && typeof imagem === 'object' ? imagem.larguraOriginal : null)
}

/**
 * Extrai a altura original do objeto imagem.
 * @param {Object|string|null} imagem
 * @returns {number|null}
 */
export function extrairAltura (imagem) {
  return (imagem && typeof imagem === 'object' ? imagem.alturaOriginal : null)
}

/**
 * Calcula dimensões proporcionais para que a imagem caiba dentro dos limites
 * máximos, sem distorcer e sem ampliar além do tamanho original.
 *
 * @param {number} larguraOriginal - Largura original em pixels
 * @param {number} alturaOriginal - Altura original em pixels
 * @param {number} maxWidth - Largura máxima em points
 * @param {number} maxHeight - Altura máxima em points
 * @returns {{ width: number, height: number }}
 */
export function calcularDimensaoProporcional (larguraOriginal, alturaOriginal, maxWidth, maxHeight) {
  if (!larguraOriginal || !alturaOriginal) return { width: maxWidth, height: null }
  const escala = Math.min(maxWidth / larguraOriginal, maxHeight / alturaOriginal, 1)
  return {
    width: Math.round(larguraOriginal * escala),
    height: Math.round(alturaOriginal * escala)
  }
}

/**
 * Gera o estilo inline de limite (max-width/max-height) para uma imagem de slot.
 * Usa os valores dinâmicos do banco (maxWidth/maxHeight, em px) com fallback para
 * os valores definidos no CSS do template. Assim, a dimensão cadastrada no banco
 * passa a valer de fato no HTML/CSS do relatório.
 *
 * @param {Object|string|null} imagem - Objeto da imagem ({ maxWidth, maxHeight, ... })
 * @param {number} [fallbackMaxWidth] - Largura máxima de fallback (px)
 * @param {number} [fallbackMaxHeight] - Altura máxima de fallback (px)
 * @returns {{ maxWidth?: string, maxHeight?: string }}
 */
export function estiloLimiteImagem (imagem, fallbackMaxWidth, fallbackMaxHeight) {
  const obj = (imagem && typeof imagem === 'object') ? imagem : null
  const maxWidth = obj?.maxWidth || fallbackMaxWidth
  const maxHeight = obj?.maxHeight || fallbackMaxHeight
  const estilo = {}
  if (maxWidth) estilo.maxWidth = `${maxWidth}px`
  if (maxHeight) estilo.maxHeight = `${maxHeight}px`
  return estilo
}

/**
 * Mapa de dimensões de página em points (1pt = 1/72").
 * Usado pelo preview HTML para simular a proporção da folha.
 */
const DIMENSOES_PAGINA = {
  A4: { w: 595, h: 842 },
  A3: { w: 842, h: 1191 },
  Letter: { w: 612, h: 792 },
  Legal: { w: 612, h: 1008 }
}

/**
 * Calcula as dimensões da página para o preview HTML,
 * respeitando pageSize e pageOrientation.
 *
 * @param {string|number[]} pageSize - Tamanho do papel ('A4','A3','Letter','Legal') ou array [w,h]
 * @param {string} pageOrientation - Orientação ('portrait','landscape')
 * @returns {{ width: number, height: number }}
 */
export function calcularDimensoesPagina (pageSize, pageOrientation) {
  let w, h

  if (Array.isArray(pageSize)) {
    w = pageSize[0]
    h = pageSize[1]
  } else if (DIMENSOES_PAGINA[pageSize]) {
    w = DIMENSOES_PAGINA[pageSize].w
    h = DIMENSOES_PAGINA[pageSize].h
  } else {
    // Fallback A4
    w = 595
    h = 842
  }

  if (pageOrientation === 'landscape') {
    ;[w, h] = [h, w]
  }

  return { width: w, height: h }
}

/**
 * Configuração padrão de página para relatórios (A4 retrato).
 * @returns {{ pageSize: string, pageOrientation: string, pageMargins: number[] }}
 */
const CONFIG_PAGINA_PADRAO = {
  pageSize: 'A4',
  pageOrientation: 'portrait',
  pageMargins: [40, 60, 40, 60]
}

/**
 * Extrai a configuração de página do ConfiguracaoJson do template.
 * Se o campo for nulo ou inválido, retorna o padrão A4 retrato.
 *
 * @param {string|Object|null} configuracaoJson - JSON string ou objeto do banco
 * @returns {{ pageSize: string, pageOrientation: string, pageMargins: number[] }}
 */
export function extrairConfiguracaoPagina (configuracaoJson) {
  if (!configuracaoJson) return { ...CONFIG_PAGINA_PADRAO }
  try {
    const config = typeof configuracaoJson === 'string' ? JSON.parse(configuracaoJson) : configuracaoJson
    return {
      pageSize: config.pageSize || CONFIG_PAGINA_PADRAO.pageSize,
      pageOrientation: config.pageOrientation || CONFIG_PAGINA_PADRAO.pageOrientation,
      pageMargins: Array.isArray(config.pageMargins) && config.pageMargins.length === 4
        ? config.pageMargins
        : CONFIG_PAGINA_PADRAO.pageMargins
    }
  } catch {
    return { ...CONFIG_PAGINA_PADRAO }
  }
}

/**
 * Gera o CSS @page dinâmico baseado na configuração da página.
 * Usado pelo Paged.js para definir tamanho, orientação e margens.
 *
 * @param {{ pageSize: string|number[], pageOrientation: string, pageMargins: number[] }} configuracaoPagina
 * @returns {string} CSS @page rule
 */
export function gerarCssPage (configuracaoPagina) {
  const { pageSize, pageOrientation, pageMargins } = configuracaoPagina
  let size
  if (Array.isArray(pageSize)) {
    size = `${pageSize[0]}pt ${pageSize[1]}pt`
  } else {
    size = pageOrientation === 'landscape'
      ? `${pageSize} landscape`
      : `${pageSize} portrait`
  }
  const [top, right, bottom, left] = pageMargins
  return `
    @page {
      size: ${size};
      margin: ${top}pt ${right}pt ${bottom}pt ${left}pt;
      @bottom-right {
        content: counter(page) " / " counter(pages);
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 9pt;
        color: #6c757d;
      }
    }
  `
}

/**
 * Gera o bloco @page para IMPRESSÃO (window.print).
 * As folhas do Paged.js já contêm as margens do relatório internamente
 * (pagebox/bleed), então a margem do papel deve ser 0 — caso contrário as
 * folhas ficam deslocadas e transbordam. O `size` dinâmico garante que o papel
 * saia na orientação correta (landscape/portrait) definida no template.
 *
 * @param {{ pageSize: string|number[], pageOrientation: string }} configuracaoPagina
 * @returns {string} CSS @page dentro de @media print
 */
export function gerarCssPrintPage (configuracaoPagina) {
  const { pageSize, pageOrientation } = configuracaoPagina
  let size
  if (Array.isArray(pageSize)) {
    size = `${pageSize[0]}pt ${pageSize[1]}pt`
  } else {
    size = pageOrientation === 'landscape'
      ? `${pageSize} landscape`
      : `${pageSize} portrait`
  }
  return `@media print { @page { size: ${size}; margin: 0; } }`
}
